import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { ResponsiveSideDialog } from 'common/components/ResponsiveSideDialog/ResponsiveSideDialog';
import { DialogComponentProps, useDialog } from 'common/hooks/useDialog';
import { useNotification } from 'common/hooks/useNotification';
import { usePeriodicityOptions } from 'common/hooks/usePeriodicityOptions';
import { AuthContext } from 'common/providers/AuthContextProvider/AuthContextProvider';
import { formatCurrency } from 'common/utils/formatUtils';
import { ButtonRow } from 'components/products/ButtonRow/ButtonRow';
import { format } from 'date-fns';
import { useCashFlowAdjustmentQuery, useUpdateCashFlowAdjustmentMutation } from 'generated/graphql';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { CashFlowAdjustmentType } from '../AddCashFlowAdjustmentDialog/AddCashFlowAdjustmentDialog';
import {
  DeleteCashFlowAdjustmentDialog,
  DeleteCashFlowAdjustmentDialogProps,
} from '../DeleteCashFlowAdjustmentDialog/DeleteCashFlowAdjustmentDialog';
import { DetailRow } from '../DetailRow/DetailRow';
import {
  EditCashFlowAdjustmentAmountDialog,
  EditCashFlowAdjustmentAmountDialogProps,
} from '../EditCashFlowAdjustmentAmountDialog/EditCashFlowAdjustmentAmountDialog';
import {
  EditCashFlowAdjustmentPeriodTypeDialog,
  EditCashFlowAdjustmentPeriodTypeDialogProps,
  PeriodType,
} from '../EditCashFlowAdjustmentPeriodTypeDialog/EditCashFlowAdjustmenPeriodTypeDialog';
import {
  EditCashFlowAdjustmentReferenceDateDialog,
  EditCashFlowAdjustmentReferenceDateDialogProps,
} from '../EditCashFlowAdjustmentReferenceDateDialog/EditCashFlowAdjustmentReferenceDateDialog';
import {
  EditCashFlowAdjustmentTextDialog,
  EditCashFlowAdjustmentTextDialogProps,
} from '../EditCashFlowAdjustmentTextDialog/EditCashFlowAdjustmentTextDialog';
import { styles } from './CashFlowAdjustmentDetailDialog.styles';

interface CashFlowAdjustmentDetailProps {
  id: string;
  type: CashFlowAdjustmentType;
}

const CashFlowAdjustmentDetailDialogContent: React.FC<CashFlowAdjustmentDetailDialogProps> = ({
  id,
  onResolve,
  type,
}) => {
  const { i18n, t } = useTranslation();
  const notify = useNotification();
  const { isAuthenticated } = useContext(AuthContext);

  const { data, loading: cashFlowAdjustmentLoading } = useCashFlowAdjustmentQuery({
    skip: !isAuthenticated,
    variables: {
      id,
    },
  });

  const amount = data?.detail.period.expectedAmount?.amount;
  const periodTypeCode = data?.detail.period.periodTypeCode;
  const periodCount = data?.detail.period.periodCount;
  const referenceDate = data?.detail.period.referenceDate;
  const text = data?.detail.text ?? '';

  const periodicityOptions = usePeriodicityOptions();

  const [updateCashFlowAdjustment, { loading: cashFlowAdjustmentUpdateLoading }] = useUpdateCashFlowAdjustmentMutation({
    onError: () => {
      notify({ message: t('common:somethingWentWrong'), type: 'error' });
    },
    refetchQueries: ['CashFlowAdjustments'],
  });

  const loading = cashFlowAdjustmentLoading || cashFlowAdjustmentUpdateLoading;

  const cashFlowAdjustmentAmountDialog = useDialog<EditCashFlowAdjustmentAmountDialogProps, number>(
    EditCashFlowAdjustmentAmountDialog,
    {
      instanceId: `set-cash-flow-adjustment-amount`,
    },
  );

  const handleAmountClick = async () => {
    const newAmount = await cashFlowAdjustmentAmountDialog.create({ amount: Math.abs(amount ?? 0) });
    await updateCashFlowAdjustment({
      variables: {
        id,
        updatedCashFlowAdjustment: {
          period: {
            expectedAmount: {
              amount: (type == CashFlowAdjustmentType.Expense ? -newAmount : newAmount) ?? 0,
              currency: 'EUR',
            },
            periodCount,
            periodTypeCode,
            referenceDate,
          },
          text,
        },
      },
    });
  };

  const cashFlowAdjustmentPeriodTypeDialog = useDialog<EditCashFlowAdjustmentPeriodTypeDialogProps, PeriodType>(
    EditCashFlowAdjustmentPeriodTypeDialog,
    {
      instanceId: `set-cash-flow-adjustment-period-type`,
    },
  );

  const handlePeriodTypeClick = async () => {
    const newPeriodType = await cashFlowAdjustmentPeriodTypeDialog.create({
      periodType: { periodCount, periodTypeCode },
    });
    await updateCashFlowAdjustment({
      variables: {
        id,
        updatedCashFlowAdjustment: {
          period: {
            expectedAmount: { amount: amount ?? 0, currency: 'EUR' },
            periodCount: newPeriodType.periodCount,
            periodTypeCode: newPeriodType.periodTypeCode,
            referenceDate,
          },
          text,
        },
      },
    });
  };

  const cashFlowAdjustmentReferenceDateDialog = useDialog<EditCashFlowAdjustmentReferenceDateDialogProps, Date>(
    EditCashFlowAdjustmentReferenceDateDialog,
    {
      instanceId: `set-cash-flow-adjustment-reference-date`,
    },
  );

  const handleReferenceDateClick = async () => {
    const newReferenceDate = await cashFlowAdjustmentReferenceDateDialog.create({ referenceDate });
    await updateCashFlowAdjustment({
      variables: {
        id,
        updatedCashFlowAdjustment: {
          period: {
            expectedAmount: { amount: amount ?? 0, currency: 'EUR' },
            periodCount,
            periodTypeCode,
            referenceDate: newReferenceDate,
          },
          text,
        },
      },
    });
  };

  const cashFlowAdjustmentTextDialog = useDialog<EditCashFlowAdjustmentTextDialogProps, string>(
    EditCashFlowAdjustmentTextDialog,
    {
      instanceId: `set-cash-flow-adjustment-text`,
    },
  );

  const handleTextClick = async () => {
    const newText = await cashFlowAdjustmentTextDialog.create({ text, type });
    await updateCashFlowAdjustment({
      variables: {
        id,
        updatedCashFlowAdjustment: {
          period: {
            expectedAmount: { amount: amount ?? 0, currency: 'EUR' },
            periodCount,
            periodTypeCode,
            referenceDate,
          },
          text: newText,
        },
      },
    });
  };

  const deleteCashFlowAdjustmentDialog = useDialog<DeleteCashFlowAdjustmentDialogProps, boolean>(
    DeleteCashFlowAdjustmentDialog,
    {
      instanceId: 'delete-cash-flow-adjustment',
    },
  );

  const handleDelete = async () => {
    const deleted = await deleteCashFlowAdjustmentDialog.create({
      id,
      type: (amount ?? 0) > 0 ? CashFlowAdjustmentType.Income : CashFlowAdjustmentType.Expense,
    });
    if (deleted) {
      await onResolve();
    }
  };

  return (
    <Stack>
      <Stack alignItems="center">
        {loading ? (
          <Skeleton height={41} variant="rectangular" />
        ) : (
          <Typography variant="headlineXL">{text}</Typography>
        )}
      </Stack>

      {loading ? (
        <Box mt={3}>
          <Skeleton height={112} variant="rectangular" />
        </Box>
      ) : (
        <Stack sx={styles.container}>
          <DetailRow<number>
            formatter={(value: number) => formatCurrency(Math.abs(value), i18n.language)}
            label={t('fintelligenceReport.cashFlowAdjustmentDetailDialog.amount')}
            onClick={handleAmountClick}
            value={amount}
          />
          <DetailRow<string>
            label={t('fintelligenceReport.cashFlowAdjustmentDetailDialog.periodType')}
            onClick={handlePeriodTypeClick}
            value={
              periodicityOptions.find(
                (option) => option.value.periodCount === periodCount && option.value.periodTypeCode === periodTypeCode,
              )?.label
            }
          />
          <DetailRow<Date>
            formatter={(value: Date) => format(value, 'd. M. yyyy')}
            label={t('fintelligenceReport.cashFlowAdjustmentDetailDialog.referenceDate')}
            onClick={handleReferenceDateClick}
            value={referenceDate}
          />
          <DetailRow<string>
            label={
              type == CashFlowAdjustmentType.Income
                ? t('fintelligenceReport.cashFlowAdjustmentDetailDialog.income.text')
                : t('fintelligenceReport.cashFlowAdjustmentDetailDialog.expense.text')
            }
            onClick={handleTextClick}
            value={text}
          />
        </Stack>
      )}

      <Box mt={8}>
        {loading ? (
          <Skeleton height={54} variant="rectangular" />
        ) : (
          <ButtonRow
            color="red.dark"
            label={t('fintelligenceReport.deleteCashFlowAdjustmentDialog.button')}
            onClick={handleDelete}
          />
        )}
      </Box>
    </Stack>
  );
};

export interface CashFlowAdjustmentDetailDialogProps
  extends CashFlowAdjustmentDetailProps,
    DialogComponentProps<void> {}

export const CashFlowAdjustmentDetailDialog: React.FC<CashFlowAdjustmentDetailDialogProps> = (props) => {
  return (
    <ResponsiveSideDialog dialogId={props.instanceId} isOpen={props.isOpen} onClose={props.onResolve}>
      <CashFlowAdjustmentDetailDialogContent {...props} />
    </ResponsiveSideDialog>
  );
};
